/**
 * Trust funding (KAN-41 / KAN-59 / KAN-85 / KAN-86).
 *
 * The screen's job is lead time: a matter caught two weeks before a trial can
 * be topped up, and one caught the day before cannot. So the ordering is by
 * risk, the horizon is visible and adjustable, and the date of the hearing is
 * on the row rather than behind a click.
 *
 * Three things are said out loud rather than implied, because each is a way a
 * trust dashboard is normally misread:
 *
 *  - **Held and overdrawn are two figures and are never added.** One matter's
 *    client money may not offset another's. A single netted total would be the
 *    exact violation this feature exists to prevent, so it does not appear here
 *    or anywhere behind it.
 *  - **A matter with no runway is not a safe matter.** "No projection" and "not
 *    enough history" are rendered as words where a number would go, never as a
 *    long runway or a zero.
 *  - **A hearing carries no assumed cost.** Nobody has given the firm expected
 *    retainer amounts per event type, so an event flags a matter only when its
 *    balance is already under the floor — printed at the foot of the page.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BURN_RATE_DAYS_PER_MONTH,
  BURN_RATE_WINDOW_MONTHS,
  DEFAULT_TRUST_FUNDING_RULE,
  EVENT_COST_ESTIMATE_NOTE,
  MATTER_EVENT_KIND_META,
  MATTER_EVENT_RULE,
  TRUST_PROJECTION_STATUS_META,
  TRUST_RECONCILIATION_STATUS_META,
  TRUST_RISK_META,
  TRUST_RISK_SIGNAL_META,
  TRUST_SORTS,
  TRUST_SORT_META,
  fromDecimalString,
  type MatterEventKind,
  type TrustFundingLine,
  type TrustFundingRule,
  type TrustFundingSummary,
  type TrustMatterDetail,
  type TrustReconciliationLine,
  type TrustReconciliationStatus,
  type TrustReconciliationSummary,
  type TrustRiskLevel,
  type TrustSort,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatCount, formatDate, formatDateTime, formatMoney, formatRelative } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

interface TrustReport {
  summary: TrustFundingSummary
  lines: TrustFundingLine[]
  practiceAreas: string[]
  attorneys: { id: string; name: string }[]
}

interface ReconciliationResponse {
  reconciliation: TrustReconciliationSummary | null
  variances: TrustReconciliationLine[]
}

interface UnallocatedResponse {
  matterScopedCount: number
  clients: {
    clientId: string
    clientName: string | null
    balanceMinor: number
    entryCount: number
    lastMovementAt: string | null
  }[]
}

/** The sentinel the API uses for "matters with no practice area recorded". */
const UNCATEGORISED = '__none__'

/**
 * Overdrawn is red and stands alone.
 *
 * `at_risk` is bronze rather than red so the two are distinguishable at a
 * glance down a column: a negative trust balance is a different kind of event
 * from a matter running low, and colouring them alike would make the page one
 * undifferentiated wall of alarm.
 */
const RISK_TONES: Record<TrustRiskLevel, Tone> = {
  overdrawn: 'critical',
  at_risk: 'warning',
  none: 'positive',
}

const RECONCILIATION_TONES: Record<TrustReconciliationStatus, Tone> = {
  reconciled: 'positive',
  variance: 'critical',
  /** Neutral, not a warning: uncompared is a known state with a known cause. */
  unavailable: 'neutral',
}

const EVENT_TONES: Record<MatterEventKind, Tone> = {
  trial: 'critical',
  hearing: 'accent',
  other: 'neutral',
}

export function TrustFunding() {
  const [sort, setSort] = useState<TrustSort>('risk')
  const [attorneyId, setAttorneyId] = useState('')
  const [practiceArea, setPracticeArea] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  /** Held as dollars because that is what a person types; sent as minor units. */
  const [floorDollars, setFloorDollars] = useState(
    String(DEFAULT_TRUST_FUNDING_RULE.minBalanceMinor / 100)
  )
  const [burnMonths, setBurnMonths] = useState(String(DEFAULT_TRUST_FUNDING_RULE.minBurnMonths))
  const [depletionDays, setDepletionDays] = useState(
    String(DEFAULT_TRUST_FUNDING_RULE.depletionHorizonDays)
  )
  const [eventDays, setEventDays] = useState(String(DEFAULT_TRUST_FUNDING_RULE.eventHorizonDays))

  const rule = useMemo(
    () => ruleFrom(floorDollars, burnMonths, depletionDays, eventDays),
    [floorDollars, burnMonths, depletionDays, eventDays]
  )

  const query = new URLSearchParams({
    sort,
    minBalanceMinor: String(rule.minBalanceMinor),
    minBurnMonths: String(rule.minBurnMonths),
    depletionHorizonDays: String(rule.depletionHorizonDays),
    eventHorizonDays: String(rule.eventHorizonDays),
  })
  if (attorneyId) query.set('attorneyId', attorneyId)
  if (practiceArea) query.set('practiceArea', practiceArea)

  const path = `/trust?${query.toString()}`

  const report = useQuery({
    queryKey: ['trust', path],
    queryFn: () => apiFetch<TrustReport>(path),
  })

  const summary = report.data?.summary ?? null
  const lines = report.data?.lines ?? []

  return (
    <>
      <PageHeader
        title="Trust funding"
        description="Which matters are running out of client money, which are already overdrawn, and which have a hearing coming up they cannot pay for. Every balance is per matter — no matter's trust is ever offset against another's."
      />

      {report.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(report.error)}
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader
          title="The rule"
          description="Which matters get flagged, and on what. Both floors apply — a matter is measured against whichever is larger — and the four thresholds travel in this page's address, so a link reproduces exactly the flagging its sender saw."
        />
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Field
              id="trust-floor"
              label="Floor"
              hint="US dollars. A balance under this is flagged whatever the matter spends."
            >
              <TextInput
                id="trust-floor"
                type="number"
                min={0}
                step={500}
                inputMode="decimal"
                value={floorDollars}
                onChange={(event) => setFloorDollars(event.target.value)}
              />
            </Field>

            <Field
              id="trust-burn-months"
              label="…or months of burn"
              hint="Whichever is larger. A matter spending $8,000 a month is not comfortable at $2,600."
            >
              <TextInput
                id="trust-burn-months"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={burnMonths}
                onChange={(event) => setBurnMonths(event.target.value)}
              />
            </Field>

            <Field
              id="trust-depletion"
              label="Runway horizon"
              hint="Days. Flag a matter projected to reach zero inside this."
            >
              <TextInput
                id="trust-depletion"
                type="number"
                min={0}
                step={5}
                inputMode="numeric"
                value={depletionDays}
                onChange={(event) => setDepletionDays(event.target.value)}
              />
            </Field>

            <Field
              id="trust-event"
              label="Hearing horizon"
              hint="Days. Flag an underfunded matter with a hearing or trial inside this."
            >
              <TextInput
                id="trust-event"
                type="number"
                min={0}
                step={5}
                inputMode="numeric"
                value={eventDays}
                onChange={(event) => setEventDays(event.target.value)}
              />
            </Field>
          </div>

          {!isDefaultRule(rule) && (
            <div className="mt-4">
              <Button size="sm" variant="ghost" onClick={resetRule}>
                Back to the firm default
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mb-6">
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              id="trust-attorney"
              label="Responsible attorney"
              hint="Who would have the funding conversation."
            >
              <Select
                id="trust-attorney"
                value={attorneyId}
                onChange={(event) => {
                  setAttorneyId(event.target.value)
                  setExpanded(null)
                }}
              >
                <option value="">Every attorney</option>
                {(report.data?.attorneys ?? []).map((attorney) => (
                  <option key={attorney.id} value={attorney.id}>
                    {attorney.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id="trust-practice-area"
              label="Matter type"
              hint="Clio's practice area. Matters with none recorded are their own group."
            >
              <Select
                id="trust-practice-area"
                value={practiceArea}
                onChange={(event) => {
                  setPracticeArea(event.target.value)
                  setExpanded(null)
                }}
              >
                <option value="">Every matter type</option>
                {(report.data?.practiceAreas ?? []).map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
                <option value={UNCATEGORISED}>Uncategorised</option>
              </Select>
            </Field>

            <Field id="trust-sort" label="Order" hint={TRUST_SORT_META[sort].description}>
              <Select
                id="trust-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as TrustSort)}
              >
                {TRUST_SORTS.map((option) => (
                  <option key={option} value={option}>
                    {TRUST_SORT_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardBody>
      </Card>

      {report.isPending && <p className="text-sm text-ink-600">Loading trust balances…</p>}

      {summary && summary.matterCount === 0 && (
        <Alert tone="info" title="No matter holds client money">
          Nothing has been posted to the trust ledger, and no matter has an upcoming hearing. Until
          Clio is connected there is nothing to monitor —{' '}
          <Link to="/integrations" className="underline">
            integration status
          </Link>{' '}
          shows what is waiting on credentials.
        </Alert>
      )}

      {summary && summary.matterCount > 0 && (
        <>
          <StatRow className="mb-6">
            <Stat
              label="Held in trust"
              value={formatMoney(summary.heldMinor)}
              hint={`Across ${formatCount(summary.matterCount)} ${
                summary.matterCount === 1 ? 'matter' : 'matters'
              }. The sum of the matters in credit — never reduced by the ones in deficit.`}
            />
            <Stat
              label="Overdrawn"
              value={formatMoney(summary.overdrawnMinor)}
              tone={summary.overdrawnMatterCount > 0 ? 'warning' : 'muted'}
              hint={
                summary.overdrawnMatterCount > 0
                  ? `${summary.overdrawnMatterCount} ${
                      summary.overdrawnMatterCount === 1 ? 'matter has' : 'matters have'
                    } a negative balance. Reported apart from the figure beside it, never subtracted from it.`
                  : 'No matter is in deficit.'
              }
            />
            <Stat
              label="At risk"
              value={formatCount(summary.atRiskCount)}
              tone={summary.atRiskCount > 0 ? 'warning' : 'muted'}
              hint={
                summary.eventFlaggedCount > 0
                  ? `${summary.eventFlaggedCount} of them with a hearing or trial inside ${summary.rule.eventHorizonDays} days.`
                  : 'Matters tripping at least one of the rules above.'
              }
            />
            <Stat
              label="Held at client level"
              value={formatMoney(summary.unallocatedMinor)}
              tone="muted"
              hint={`For ${formatCount(summary.unallocatedClientCount)} ${
                summary.unallocatedClientCount === 1 ? 'client' : 'clients'
              }, against no matter. Reported and never spread — which matter it funds is Finance's decision.`}
            />
          </StatRow>

          <Reconciliation summary={summary.reconciliation} />

          <Card>
            <CardHeader
              title="Matters"
              description={`${TRUST_SORT_META[sort].label}. Expand a row for the full trust ledger, its running balance, and the matter's calendar.`}
              actions={<Badge tone="neutral">{lines.length}</Badge>}
            />

            <TableWrap>
              <thead>
                <tr>
                  <Th>Matter</Th>
                  <Th className="text-right">Balance</Th>
                  <Th className="text-right">Burn / month</Th>
                  <Th className="text-right">Runway</Th>
                  <Th>Next hearing</Th>
                  <Th>Flagged for</Th>
                  <Th>Clio</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <EmptyRow colSpan={8}>
                    No matter matches these filters. The thresholds and the filters are separate —
                    a matter excluded here may still be flagged for somebody else.
                  </EmptyRow>
                )}

                {lines.map((line) => (
                  <MatterRow
                    key={line.matterId}
                    line={line}
                    expanded={expanded === line.matterId}
                    onToggle={() =>
                      setExpanded(expanded === line.matterId ? null : line.matterId)
                    }
                  />
                ))}
              </tbody>
            </TableWrap>
          </Card>

          <Unallocated />

          <div className="mt-6 max-w-3xl space-y-3 border-t border-ink-200 pt-5 text-xs leading-relaxed text-ink-600">
            <p>
              <strong className="font-semibold text-ink-800">Balances stand alone.</strong> Each
              matter's balance is summed from its own trust movements and never offset against
              another's — the two headline figures above are held and overdrawn, and they are not
              added. A negative balance is reported prominently rather than clamped to zero: it is
              either a real overdraw or a sync fault, and both need somebody today.
            </p>
            <p>
              <strong className="font-semibold text-ink-800">Runway is measured, not assumed.</strong>{' '}
              Burn is the average of what left trust over the trailing {BURN_RATE_WINDOW_MONTHS}{' '}
              months — a single quiet month before a trial would otherwise report years of runway.
              The days shown are balance ÷ burn × {BURN_RATE_DAYS_PER_MONTH}, so the arithmetic can
              be checked by hand. A matter with nothing leaving trust shows no projection rather
              than an unlimited one, and a matter too new to have a rate is marked as such rather
              than given a figure borrowed from other matters.
            </p>
            <p>
              <strong className="font-semibold text-ink-800">Hearings carry no assumed cost.</strong>{' '}
              {EVENT_COST_ESTIMATE_NOTE}
            </p>
            <p>
              <strong className="font-semibold text-ink-800">How an event is recognised.</strong>{' '}
              {MATTER_EVENT_RULE} The entry's own title is shown beside every flag, so a
              misclassification is visible rather than silent.
            </p>
          </div>
        </>
      )}
    </>
  )

  function resetRule() {
    setFloorDollars(String(DEFAULT_TRUST_FUNDING_RULE.minBalanceMinor / 100))
    setBurnMonths(String(DEFAULT_TRUST_FUNDING_RULE.minBurnMonths))
    setDepletionDays(String(DEFAULT_TRUST_FUNDING_RULE.depletionHorizonDays))
    setEventDays(String(DEFAULT_TRUST_FUNDING_RULE.eventHorizonDays))
  }
}

function MatterRow({
  line,
  expanded,
  onToggle,
}: {
  line: TrustFundingLine
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className={expanded ? 'bg-brand-50/50' : undefined}>
        <Td>
          <span className="block text-ink-900">{line.matterDisplayNumber}</span>
          <span className="block text-xs text-ink-600">
            {[line.clientName, line.attorneyName, line.practiceArea].filter(Boolean).join(' · ') ||
              'No client or attorney recorded'}
          </span>
        </Td>

        <Td className="text-right tabular-nums">
          {/*
            * Negative balances are coloured and signed rather than shown as a
            * magnitude. A minus sign in a currency column is easy to miss, and
            * this is the one figure on the screen nobody may miss.
            */}
          <span className={line.balanceMinor < 0 ? 'font-semibold text-red-700' : undefined}>
            {formatMoney(line.balanceMinor)}
          </span>
          <span className="mt-0.5 block text-xs text-ink-600">
            floor {formatMoney(line.floorMinor)}
          </span>
        </Td>

        <Td className="text-right tabular-nums">
          {line.burn.status === 'projected' ? (
            <>
              {formatMoney(line.burn.monthlyBurnMinor)}
              <span className="mt-0.5 block text-xs text-ink-600">
                {formatCount(line.burn.windowMovements)} over {BURN_RATE_WINDOW_MONTHS} mo
              </span>
            </>
          ) : (
            <span className="text-ink-500">—</span>
          )}
        </Td>

        <Td className="text-right">
          {line.burn.status === 'projected' && line.burn.daysRemaining !== null ? (
            <>
              <span className="tabular-nums text-ink-900">
                {formatCount(line.burn.daysRemaining)} d
              </span>
              <span className="mt-0.5 block text-xs text-ink-600">
                {/*
                  * Date only. The runway is derived from a three-month average,
                  * so naming an hour would claim a precision the projection
                  * does not have — and a finance figure gets quoted at whatever
                  * precision it is printed at.
                  */}
                {formatDate(line.burn.depletionOn)}
              </span>
            </>
          ) : (
            /*
             * Words where a number would go. "No projection" and "not enough
             * history" are different statements and neither is a long runway;
             * rendering either as a dash or a large number is how a dormant
             * matter and a brand-new one both come to look safe.
             */
            <span
              className="text-xs text-ink-500"
              title={TRUST_PROJECTION_STATUS_META[line.burn.status].description}
            >
              {TRUST_PROJECTION_STATUS_META[line.burn.status].label}
            </span>
          )}
        </Td>

        <Td>
          {line.nextEvent ? (
            <>
              <span className="flex items-center gap-1.5">
                <Badge tone={EVENT_TONES[line.nextEvent.kind]}>
                  {MATTER_EVENT_KIND_META[line.nextEvent.kind].label}
                </Badge>
                {line.daysToNextEvent !== null && (
                  <span className="text-xs tabular-nums text-ink-700">
                    in {formatCount(line.daysToNextEvent)} d
                  </span>
                )}
              </span>
              <span className="mt-0.5 block max-w-[16rem] truncate text-xs text-ink-600" title={line.nextEvent.summary}>
                {line.nextEvent.summary || 'Untitled entry'}
              </span>
            </>
          ) : (
            <span className="text-xs text-ink-500">None scheduled</span>
          )}
        </Td>

        <Td>
          <span title={TRUST_RISK_META[line.riskLevel].description}>
            <Badge tone={RISK_TONES[line.riskLevel]}>{TRUST_RISK_META[line.riskLevel].label}</Badge>
          </span>
          {line.signals.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {line.signals.map((signal) => (
                <li
                  key={signal}
                  className="text-xs text-ink-600"
                  title={TRUST_RISK_SIGNAL_META[signal].description}
                >
                  {TRUST_RISK_SIGNAL_META[signal].label}
                </li>
              ))}
            </ul>
          )}
        </Td>

        <Td>
          <span title={TRUST_RECONCILIATION_STATUS_META[line.reconciliation].description}>
            <Badge tone={RECONCILIATION_TONES[line.reconciliation]}>
              {TRUST_RECONCILIATION_STATUS_META[line.reconciliation].label}
            </Badge>
          </span>
          {line.reconciliationVarianceMinor !== null &&
            line.reconciliationVarianceMinor !== 0 && (
              <span className="mt-1 block text-xs tabular-nums text-red-700">
                {formatMoney(line.reconciliationVarianceMinor)} apart
              </span>
            )}
        </Td>

        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Ledger'}
          </Button>
        </Td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <MatterDetail matterId={line.matterId} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The drill-down KAN-41 and KAN-59 both ask for: the full trust ledger with a
 * running balance, the matter's calendar, and how the comparison against Clio
 * has gone.
 *
 * Loaded on expand rather than with the page — a firm-wide dashboard would
 * otherwise fetch every trust movement in the firm to show one matter's.
 */
function MatterDetail({ matterId }: { matterId: string }) {
  const detail = useQuery({
    queryKey: ['trust-matter', matterId],
    queryFn: () => apiFetch<TrustMatterDetail>(`/trust/matters/${matterId}`),
  })

  if (detail.isPending) return <p className="text-sm text-ink-600">Loading the trust ledger…</p>
  if (detail.error) return <Alert tone="error">{messageFor(detail.error)}</Alert>
  if (!detail.data) return null

  const { ledger, events, reconciliationHistory } = detail.data
  const variance =
    detail.data.clioReportedMinor === null
      ? null
      : detail.data.balanceMinor - detail.data.clioReportedMinor

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="Our balance"
          value={formatMoney(detail.data.balanceMinor)}
          hint={`Summed from ${formatCount(ledger.length)} ${
            ledger.length === 1 ? 'movement' : 'movements'
          } below. The last running balance equals this figure.`}
        />
        <Stat
          label="Clio's balance"
          value={
            detail.data.clioReportedMinor === null
              ? 'Not reported'
              : formatMoney(detail.data.clioReportedMinor)
          }
          tone={detail.data.clioReportedMinor === null ? 'muted' : 'default'}
          hint={
            detail.data.clioReportedMinor === null
              ? 'Clio sent no trust balance for this matter, so there is nothing to compare against. Shown as unreported rather than as zero.'
              : `As at ${formatDateTime(detail.data.clioReportedAt)}.`
          }
        />
        <Stat
          label="Difference"
          value={variance === null ? '—' : formatMoney(variance)}
          tone={variance === null ? 'muted' : variance === 0 ? 'default' : 'warning'}
          hint="Ours minus Clio's. There is no tolerance: both are exact minor units, so any difference is a real one."
        />
      </div>

      {events.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-ink-200 bg-white">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                <Th>Upcoming</Th>
                <Th>Entry</Th>
                <Th>Reads as</Th>
                <Th>Source</Th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <Td className="whitespace-nowrap">
                    {/* An all-day entry has no hour to show, so none is shown. */}
                    {event.allDay
                      ? formatDate(event.startsAt)
                      : formatDateTime(event.startsAt)}
                  </Td>
                  <Td>{event.summary || <span className="text-ink-500">Untitled entry</span>}</Td>
                  <Td>
                    <span title={MATTER_EVENT_KIND_META[event.kind].description}>
                      <Badge tone={EVENT_TONES[event.kind]}>
                        {MATTER_EVENT_KIND_META[event.kind].label}
                      </Badge>
                    </span>
                  </Td>
                  <Td>
                    <code className="font-mono text-[11px] text-ink-500">
                      {event.sourceSystem}
                      {event.sourceId ? ` ${event.sourceId}` : ''}
                    </code>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-ink-200 bg-white">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Movement</Th>
              <Th className="text-right">Amount</Th>
              <Th className="text-right">Balance after</Th>
              <Th>Source</Th>
            </tr>
          </thead>
          <tbody>
            {ledger.length === 0 && (
              <EmptyRow colSpan={5}>
                No trust movement has ever been posted against this matter. It is on this dashboard
                because it has a hearing coming up, which is the case a balance-driven list would
                miss entirely.
              </EmptyRow>
            )}

            {ledger.map((row) => (
              <tr key={row.id}>
                <Td className="whitespace-nowrap">{formatDateTime(row.occurredAt)}</Td>
                <Td>
                  <span className="block text-ink-800">{row.entryType.replace(/_/g, ' ')}</span>
                  {row.description && (
                    <span className="block text-ink-600">{row.description}</span>
                  )}
                  {row.reversesEntryId && (
                    <span className="block text-ink-500">
                      Correction — reverses an earlier entry rather than editing it
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums">
                  <span className={row.amountMinor < 0 ? 'text-ink-800' : 'text-emerald-800'}>
                    {formatMoney(row.amountMinor)}
                  </span>
                </Td>
                <Td className="text-right tabular-nums">
                  <span
                    className={
                      row.runningBalanceMinor < 0 ? 'font-semibold text-red-700' : undefined
                    }
                  >
                    {formatMoney(row.runningBalanceMinor)}
                  </span>
                </Td>
                <Td>
                  <code className="font-mono text-[11px] text-ink-500">
                    {row.sourceSystem}
                    {row.sourceId ? ` ${row.sourceId}` : ''}
                  </code>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs leading-relaxed text-ink-600">
        The running balance is recomputed from the movements on every read, in value-date order and
        then by the order the rows were written here. It is not the{' '}
        <code className="font-mono">balance_after</code> recorded on each entry, which is a
        client-level total frozen at the moment it synced — the ledger is append-only, so a
        backdated movement can never rewrite the rows above it. A correction appears as a reversal
        and a restatement rather than as an edit.
      </p>

      {reconciliationHistory.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
            Reconciliation history
          </p>
          <ul className="space-y-1 text-xs text-ink-700">
            {reconciliationHistory.map((check, index) => (
              <li key={`${check.checkedAt}-${index}`} className="flex flex-wrap items-center gap-2">
                <Badge tone={RECONCILIATION_TONES[check.status]}>
                  {TRUST_RECONCILIATION_STATUS_META[check.status].label}
                </Badge>
                <span className="tabular-nums">
                  {formatMoney(check.computedMinor)} here ·{' '}
                  {check.clioReportedMinor === null
                    ? 'nothing from Clio'
                    : `${formatMoney(check.clioReportedMinor)} at Clio`}
                </span>
                <span className="text-ink-500">{formatRelative(check.checkedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * The reconciliation against Clio.
 *
 * Its own card and its own request, so a slow variance list does not hold up
 * the balances above it. The summary figures come from the dashboard's own
 * response — they are part of the same pass — and only the matters that
 * disagree are fetched, because a reconciliation screen exists to show what is
 * wrong and a page of rows saying "fine" is how the two that are not get
 * missed.
 */
function Reconciliation({ summary }: { summary: TrustReconciliationSummary | null }) {
  const [open, setOpen] = useState(false)

  const result = useQuery({
    queryKey: ['trust-reconciliation'],
    queryFn: () => apiFetch<ReconciliationResponse>('/trust/reconciliation'),
    enabled: open,
  })

  if (!summary) {
    return (
      <Alert tone="info" className="mb-6" title="Not yet compared against Clio">
        No reconciliation pass has run. It runs off the back of a Clio trust or matter sync — both
        halves of the comparison come from there — so this stays empty until Clio is connected.
        Finance can also run one by hand with{' '}
        <code className="font-mono text-[11px]">node ace trust:reconcile</code>.
      </Alert>
    )
  }

  return (
    <Card className="mb-6">
      <CardHeader
        title="Against Clio"
        description="Our balances, summed transaction by transaction from the trust ledger, against the figures Clio reports on each matter. Two independent readings of the same client money — neither is preferred, and a disagreement is recorded rather than resolved in our favour."
        actions={
          <Badge tone={summary.varianceCount > 0 ? 'critical' : 'positive'}>
            {summary.varianceCount > 0
              ? `${summary.varianceCount} disagree`
              : 'Every compared matter agrees'}
          </Badge>
        }
      />
      <CardBody>
        <StatRow>
          <Stat
            label="Agree"
            value={formatCount(summary.reconciledCount)}
            hint="Identical to the cent. There is no tolerance on this comparison."
          />
          <Stat
            label="Disagree"
            value={formatCount(summary.varianceCount)}
            tone={summary.varianceCount > 0 ? 'warning' : 'muted'}
            hint={`${formatMoney(summary.absoluteVarianceMinor)} apart in total, counted absolutely — an over and an under are two problems, not none.`}
          />
          <Stat
            label="Not compared"
            value={formatCount(summary.unavailableCount)}
            tone="muted"
            hint="Clio reported no trust balance for these matters. Shown as uncompared rather than as agreement."
          />
          <Stat
            label="Checked"
            value={formatRelative(summary.checkedAt)}
            tone="muted"
            hint="Runs after any Clio sync that could move either side of the comparison."
          />
        </StatRow>

        {summary.varianceCount > 0 && (
          <div className="mt-4">
            <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
              {open ? 'Hide the matters that disagree' : 'Show the matters that disagree'}
            </Button>

            {open && result.isPending && (
              <p className="mt-3 text-sm text-ink-600">Loading variances…</p>
            )}
            {open && result.error && (
              <Alert tone="error" className="mt-3">
                {messageFor(result.error)}
              </Alert>
            )}

            {open && result.data && (
              <div className="mt-3 overflow-x-auto rounded-md border border-ink-200 bg-white">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <Th>Matter</Th>
                      <Th className="text-right">Here</Th>
                      <Th className="text-right">At Clio</Th>
                      <Th className="text-right">Difference</Th>
                      <Th>Clio figure dated</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.variances.map((line) => (
                      <tr key={line.matterId}>
                        <Td>
                          <span className="block text-ink-900">{line.matterDisplayNumber}</span>
                          {line.clientName && (
                            <span className="block text-ink-600">{line.clientName}</span>
                          )}
                        </Td>
                        <Td className="text-right tabular-nums">
                          {formatMoney(line.computedMinor)}
                        </Td>
                        <Td className="text-right tabular-nums">
                          {formatMoney(line.clioReportedMinor)}
                        </Td>
                        <Td className="text-right font-semibold tabular-nums text-red-700">
                          {formatMoney(line.varianceMinor)}
                        </Td>
                        <Td className="whitespace-nowrap">
                          {formatDateTime(line.clioReportedAt)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * Client-level trust: money held for a client against no matter at all.
 *
 * Collapsed by default because it is a footnote to the dashboard rather than
 * part of it, but present because the alternative is a headline figure nobody
 * can break down. It is never spread across a client's matters — which bill it
 * pays is Finance's decision, the same rule the forecast and the AR report
 * follow.
 */
function Unallocated() {
  const [open, setOpen] = useState(false)

  const result = useQuery({
    queryKey: ['trust-unallocated'],
    queryFn: () => apiFetch<UnallocatedResponse>('/trust/unallocated'),
    enabled: open,
  })

  return (
    <Card className="mt-6">
      <CardHeader
        title="Held at client level"
        description="Trust posted against a client but no matter. Reported on its own and never allocated — spreading it across a client's matters would put money against a file nobody chose."
        actions={
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      />

      {open && (
        <CardBody>
          {result.isPending && <p className="text-sm text-ink-600">Loading…</p>}
          {result.error && <Alert tone="error">{messageFor(result.error)}</Alert>}

          {result.data && result.data.clients.length === 0 && (
            <p className="text-sm text-ink-600">
              Every trust movement is attributed to a matter. Nothing sits at client level.
            </p>
          )}

          {result.data && result.data.clients.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-ink-200 bg-white">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <Th>Client</Th>
                    <Th className="text-right">Balance</Th>
                    <Th className="text-right">Movements</Th>
                    <Th>Last movement</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.data.clients.map((row) => (
                    <tr key={row.clientId}>
                      <Td>{row.clientName ?? `Client ${row.clientId.slice(0, 8)}`}</Td>
                      <Td className="text-right tabular-nums">
                        <span
                          className={row.balanceMinor < 0 ? 'font-semibold text-red-700' : undefined}
                        >
                          {formatMoney(row.balanceMinor)}
                        </span>
                      </Td>
                      <Td className="text-right tabular-nums">{formatCount(row.entryCount)}</Td>
                      <Td className="whitespace-nowrap">{formatDateTime(row.lastMovementAt)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      )}
    </Card>
  )
}

/**
 * The rule the controls describe.
 *
 * The dollar floor is parsed through the shared money parser rather than
 * `parseFloat`, so a dollar figure never becomes a float on its way to becoming
 * cents: `19.99` through `Number` then `* 100` is `1998.9999…`. Anything this
 * cannot parse falls back to the firm default, matching what the API does with
 * the same input — otherwise the screen would flag a different set of matters
 * from the one the endpoint would.
 */
function ruleFrom(
  floorDollars: string,
  burnMonths: string,
  depletionDays: string,
  eventDays: string
): TrustFundingRule {
  let minBalanceMinor = DEFAULT_TRUST_FUNDING_RULE.minBalanceMinor
  try {
    if (floorDollars.trim() !== '') {
      minBalanceMinor = Math.max(0, fromDecimalString(floorDollars))
    }
  } catch {
    // Unparseable, so the default stands. See above.
  }

  return {
    minBalanceMinor,
    minBurnMonths: wholeNumber(burnMonths, DEFAULT_TRUST_FUNDING_RULE.minBurnMonths),
    depletionHorizonDays: wholeNumber(
      depletionDays,
      DEFAULT_TRUST_FUNDING_RULE.depletionHorizonDays
    ),
    eventHorizonDays: wholeNumber(eventDays, DEFAULT_TRUST_FUNDING_RULE.eventHorizonDays),
  }
}

function wholeNumber(input: string, fallback: number): number {
  const trimmed = input.trim()
  if (trimmed === '') return fallback
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : fallback
}

function isDefaultRule(rule: TrustFundingRule): boolean {
  return (
    rule.minBalanceMinor === DEFAULT_TRUST_FUNDING_RULE.minBalanceMinor &&
    rule.minBurnMonths === DEFAULT_TRUST_FUNDING_RULE.minBurnMonths &&
    rule.depletionHorizonDays === DEFAULT_TRUST_FUNDING_RULE.depletionHorizonDays &&
    rule.eventHorizonDays === DEFAULT_TRUST_FUNDING_RULE.eventHorizonDays
  )
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}
